// ─────────────────────────────────────────────────────────────────────────────
//  MSG-DELIV-1 (22/07) — LIVRAISON + RETRY des messages, isolés du corps de Messenger.
//
//  Objectif d'architecte : faire de la fiabilité d'envoi une brique À PART, avec un
//  CŒUR PUR testable unitairement (aujourd'hui la chaîne pending→sent→failed→retry
//  n'était prouvée que par e2e live). Fondation d'un futur outbox (envoi résilient,
//  dédup par id client) : le jour venu, il se branche ici sans re-chirurgie.
//
//  ⚠️ EXTRACTION ISO-COMPORTEMENT (VERBATIM) : mêmes requêtes, mêmes octets
//  (persistConvoAndMessage/uploadChatImage appelés à l'identique, dans le même ordre),
//  même temps réel (aucune émission changée), même UI. Les 4 fonctions PURES ci-dessous
//  capturent EXACTEMENT les décisions qui vivaient dans `send`/`stampSentSeq`/`retry`.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from "../00-boot.jsx";
import { safetyTextIssue, texteRefuse } from "../01-taxonomy-cities.jsx";
import { newUUID } from "../05-ui-atoms-theme.jsx";
import { lanacReadSeqMap, lanacReadWatermark, persistConvoAndMessage } from "../07-trust-moderation.jsx";
import { apresEchec, attenteDepuis, aUneRelance, instantDArmement, prochaineRelance, reculDuSignal, relancesPerimees, GESTE_APRES_MS, RELANCE_ESSAI_MS } from "./relance.jsx";

// ── Cœur PUR (aucun effet, aucune dépendance React) — testé par messenger-delivery.test.mjs ──

// Décision d'envoi : reproduit à l'IDENTIQUE les gardes de `send`. Retourne {ok, error} où
//  error = message à afficher (setAttachErr) ou null (cas vide/bloqué = refus SILENCIEUX, comme avant).
//  `safetyTextIssue`, `M` injectés → fonction déterministe et testable avec des stubs.
//  (lot D · D6-j) `lectureSeule` : le fil d'autrui (horsDuFil) — refus SILENCIEUX, avant toute bulle optimiste et tout téléversement
//   (le composeur n'y est pas rendu ; défense en profondeur pour l'envoi ET le renvoi).
export function decideSend({ text, hasImage, actId, active, exchangeBlocked, safetyTextIssue, M, lectureSeule = false }) {
  const t = (text || "").trim();
  if ((!t && !hasImage) || !actId || !active || exchangeBlocked || lectureSeule) return { ok: false, error: null };
  //  (B-MOTS, 02/11/2026) `issue` = l'expression BLOQUÉE trouvée (celle que la base refuserait) — le texte reste dans le composeur, sans menace
  const issue = safetyTextIssue(t);
  if (issue) return { ok: false, error: M(`Message non envoyé : « ${issue} » n'est pas accepté sur LANAC. Reformulez votre message.`, `Message not sent: “${issue}” is not allowed on LANAC. Please rephrase your message.`, `Nachricht nicht gesendet: „${issue}“ ist auf LANAC nicht erlaubt. Bitte formulieren Sie Ihre Nachricht um.`, `Mensagem não enviada: «${issue}» não é permitido no LANAC. Reformule a sua mensagem.`) };
  // (14/09, C7 · T5) Plus de plafond de « revue d'accès » : ce que d'autres signalent ne limite l'envoi de personne.
  return { ok: true, error: null };
}

// Construit le message OPTIMISTE (aperçu local immédiat). `id`/`now` injectés = déterminisme de test.
//  ⭐ `pending:true` (28/08, audit) : AVANT ce marqueur, un envoi interrompu EN VOL (onglet
//  fermé pendant la requête) était sauvé au débounce SANS `failed` ⇒ faux « envoyé »
//  permanent, invisible de l'outbox. Le marqueur ne s'efface qu'à la RÉPONSE du serveur
//  (succès → retiré, échec → failed) : un pending qui survit au rechargement est un envoi
//  au sort INCONNU — et comme le rejeu est idempotent par id (23505 = succès), le rejouer
//  est TOUJOURS correct, qu'il soit arrivé ou non.
export function buildOptimisticMessage({ id, from, text, image, replyTo, preview, now }) {
  return {
    id, from, text,
    image: image ? { url: image.url, name: image.name } : null,
    replyTo: replyTo ? { id: replyTo.id, from: replyTo.from, text: preview(replyTo).slice(0, 120) } : null,
    ts: now, read: false, pending: true,
  };
}

// Le résultat de persist porte-t-il une erreur ? (→ marque le message `failed`).
export function persistFailed(res) { return !!(res && res.error); }

// C7 · bloqueurs T9 (16/09) : le serveur refuse un message écrit dans un fil bloqué (P0001 « conversation_blocked »). Ce n'est
//  PAS un échec à rejouer : le message est marqué `refused` — dit sur sa ligne, sans « Renvoyer », jamais rejoué par l'outbox
//  (rejouer une règle serait frapper à une porte fermée, au boot et à chaque retour du réseau).
export function persistRefusedByBlock(res) { return !!(res && res.error && String(res.error.message || "") === "conversation_blocked"); }
// (B-MOTS, 02/11/2026) la base refuse un texte qui porte une expression BLOQUÉE (22023 « texte_refuse », l'expression en indice) : une RÈGLE, comme
//  le fil bloqué — `refused:"texte"` et l'expression (`refusMot`), dits sur la ligne, jamais rejoués (ni au démarrage, ni au retour du réseau).
export function persistRefusedByText(res) { return res && res.error ? texteRefuse(res.error) : null; }
// L'état de la ligne pour un refus de RÈGLE (fil bloqué, texte refusé), ou null pour tout autre résultat.
export function refusDuPersist(res) {
  if (persistRefusedByBlock(res)) return { refused: "blocked", failed: false, pending: false };
  const mot = persistRefusedByText(res);
  return mot !== null ? { refused: "texte", refusMot: mot, failed: false, pending: false } : null;
}

//  ⭐ C7 · T25 — LA CADENCE (19/09/2026). Un refus de cadence n'est NI une panne NI une règle définitive : il est
//  TEMPORAIRE, et le serveur dit exactement combien de secondes attendre. La maison emploie déjà les SQLSTATE
//  `429xx` pour ses limites (`lanacRpc` les traduit en « rate-limited » depuis les signalements) : on lit la même
//  famille, plutôt que d'ajouter un code à connaître. `hint` porte la phrase que le membre lira, `details` les
//  secondes — quand le serveur les donne.
export function persistRefusedByRate(res) {
  const code = String((res && res.error && res.error.code) || "");
  if (!/^429[0-9A-Z]{2}$/.test(code)) return null;
  const brut = String((res.error.details ?? "")).trim();
  return { phrase: String(res.error.hint || "") || null, secondes: /^[0-9]+$/.test(brut) ? Number(brut) : null };
}

//  ⭐ C7 · T27 — DEUX SECONDES PAR DÉFAUT si le serveur ne chiffre pas son refus. On ne devine pas un DÉLAI
//  (le recul exponentiel de Signal et WhatsApp fait exactement cela) : on prend la valeur du serveur quand
//  elle est là, et une valeur plancher honnête quand elle manque. Bornée à cinq minutes : un message ne doit
//  pas dormir une heure parce qu'un serveur aurait annoncé un chiffre aberrant.
export function delaiDuRefus(info) {
  const s = info && Number(info.secondes);
  if (!Number.isFinite(s) || s <= 0) return 2000;
  return Math.min(Math.round(s * 1000), 300000);
}

// seq serveur exploitable dans la réponse d'insert (M5c) ? (→ estampille le message optimiste).
export function extractSentSeq(res) {
  const sq = Number(res && res.data && res.data[0] && res.data[0].seq);
  return Number.isFinite(sq) ? sq : null;
}

// ── M2 · OUTBOX (28/07/2026) — L'ENVOI NE SE PERD PLUS ────────────────────────────
//  AVANT : réseau coupé ⇒ le message s'affiche « ⚠ Non envoyé · Renvoyer » et attend un
//  CLIC. C'est le comportement que toutes les messageries modernes ont abandonné.
//
//  ⭐ AUCUNE FILE PARALLÈLE — et c'est LE choix de conception. Une seconde file finirait
//  par diverger de ce que l'écran montre (message renvoyé mais toujours barré, ou l'inverse).
//  On rejoue depuis la SOURCE UNIQUE, `convos` : un envoi raté y porte déjà `failed:true`,
//  et `trimConvoForCache` conserve ce drapeau ⇒ les échecs SURVIVENT DÉJÀ au rechargement.
//  L'outbox n'est donc pas un stockage, c'est une POLITIQUE DE REJEU.
//
//  ⭐ IDEMPOTENT PAR CONSTRUCTION, pas par surveillance : `newUUID()` est appelé UNE fois,
//  dans le message optimiste ; rejouer le MÊME objet réinsère le MÊME id, et la passerelle
//  traite un 23505 à l'INSERT comme un SUCCÈS (contrat prouvé le 27/07, filet 9/9). Un
//  double rejeu ne peut donc pas produire un doublon.
//
//  BORNES ASSUMÉES — au plus `max` messages par passe (on ne réveille pas un troupeau), et
//  rien de plus vieux que `maxAgeMs` : ressusciter silencieusement un message écrit il y a
//  trois jours serait une surprise, pas un service. Au-delà, le bouton « Renvoyer » reste.
//  Ordre d'ÉMISSION préservé (tri par ts) : on renvoie dans l'ordre où l'utilisateur a écrit.
//  ⭐ CONTRAT ÉLARGI (28/08, audit) — deux populations rejouables :
//   · `failed === true` : l'échec AVÉRÉ (comme depuis M2) ;
//   · `pending === true` ET plus vieux que `pendingGraceMs` : l'envoi INTERROMPU EN VOL
//     (l'onglet s'est fermé avant la réponse). La grâce évite de rejouer un envoi encore
//     en cours dans la session vivante ; l'idempotence par id rend le rejeu d'un envoi
//     finalement ARRIVÉ inoffensif (23505 = succès).
//   · `imageLost === true` est EXCLU : une photo dont l'aperçu a été strippé du cache ne
//     doit JAMAIS repartir amputée en silence — elle reste barrée, au geste de l'utilisateur.
export function collectOutbox(convos, meId, { now, maxAgeMs = 86400000, max = 20, pendingGraceMs = 15000, signal = false, definitifs = true, enVol = true } = {}) {
  if (!meId || !Number.isFinite(now)) return [];
  const out = [];
  for (const c of convos || []) {
    if (!c || !Array.isArray(c.messages)) continue;
    if (!(c.participants || []).includes(meId)) continue;   // jamais la conversation d'autrui
    const file = [];                                         // A42 : mes messages EN RELANCE dans ce fil (traités après la boucle)
    for (const m of c.messages) {
      if (!m) continue;
      if (m.from !== meId) continue;                         // on ne rejoue que SES propres envois
      if (m.imageLost === true) continue;                    // jamais d'amputation silencieuse
      if (m.refused) continue;                               // refusé par une règle (fil bloqué) : jamais rejoué
      const ts = Number(m.ts || 0);
      const age = now - ts;
      if (!Number.isFinite(age) || age < 0 || age > maxAgeMs) continue;
      //  ⭐ A42 — LA RELANCE (13/10). Un message qu'une PANNE a retenu (`relance` : "reseau", "serveur", ou "file" derrière un
      //   autre) n'est ni un échec ni un envoi en vol : il attend son tour dans la file de SON fil, traitée après cette boucle.
      if (typeof m.relance === "string") { file.push(m); continue; }
      //  Un refus DÉFINITIF (`definitif`, posé par apresEchec : une règle a dit non) reste rejoué au démarrage et au retour du
      //   réseau, comme avant — jamais par l'horloge ni par un signal (`definitifs: false`) : on ne frappe pas chaque minute.
      const echec = m.failed === true && (definitifs || m.definitif !== true);
      //  A42 — UN ENVOI EN VOL n'est rejoué que par les passes d'avant (démarrage, `online` : `enVol`), pas par les signaux ni
      //   par l'horloge ; et JAMAIS tant que sa photo est encore portée en ligne (`data:` : son téléversement est en cours —
      //   la rejouer enverrait des mégaoctets de base64 à la place de l'adresse ; après un rechargement, `imageLost` le dit).
      const enLigne = !!(m.image && typeof m.image.url === "string" && m.image.url.indexOf("data:") === 0);
      const enVolPerime = enVol && m.pending === true && age > pendingGraceMs && !enLigne;
      //  ⭐ C7 · T27 — LE DIFFÉRÉ. Le serveur a dit « réessayez à tel instant » ; `differe` le porte, et il
      //   survit au redémarrage (le cache des conversations garde le message tel quel). Il n'est MÛR qu'à son
      //   instant : l'envoyer avant, c'est payer une requête pour s'entendre dire ce qu'on sait déjà.
      //  ⚠️ Typage STRICT, comme `failed === true` : un cache mal typé (`differe: "123"`) ne doit pas
      //   ressusciter un message. Un nombre, fini, positif — rien d'autre.
      const differe = typeof m.differe === "number" && Number.isFinite(m.differe) && m.differe > 0 ? m.differe : 0;
      const differeEchu = differe > 0 && now >= differe;
      if (!echec && !enVolPerime && !differeEchu) continue;
      out.push({ convo: c, message: m, ts });
    }
    //  ⭐ A42 — LA FILE D'UN FIL N'AVANCE QUE PAR SA TÊTE (l'ordre d'écriture est tenu ; Signal : une file par destinataire).
    //   Mûr : un message en file derrière un autre ("file"), ou dont l'instant du prochain essai est passé, ou n'importe lequel
    //   quand un SIGNAL dit que le réseau est revenu (démarrage, `online`, premier plan, liaison temps réel rétablie) — sauf si
    //   le serveur lui-même a dit d'attendre (`differe`, T27). Dès qu'un message n'est pas mûr, tout ce qui le suit attend.
    if (file.length) {
      file.sort((a, b) => Number(a.ts || 0) - Number(b.ts || 0) || (String(a.id) < String(b.id) ? -1 : 1));
      for (const m of file) {
        const interdit = typeof m.differe === "number" && Number.isFinite(m.differe) && m.differe > now;
        const instant = typeof m.reessai === "number" && Number.isFinite(m.reessai) && m.reessai > now;
        if (interdit || (m.relance !== "file" && instant && !signal)) break;
        out.push({ convo: c, message: m, ts: Number(m.ts || 0) });
      }
    }
  }
  out.sort((a, b) => a.ts - b.ts || (String(a.message.id) < String(b.message.id) ? -1 : 1));
  return out.slice(0, max);
}

// Kill-switch, même idiome que les autres bascules du dépôt : "0" désarme sans redéploiement.
const outboxArme = () => { try { return localStorage.getItem("lanac:outbox") !== "0"; } catch (e) { return true; } };

//  ⭐ A42 — CE QUI SUIT UN ÉCHEC, décidé en UN endroit pour l'envoi, le renvoi au geste et le rejeu. Une cadence : le serveur a
//   dit QUAND (T27, enfin aussi pour l'envoi direct). Une panne (réseau, serveur) : le message attend, calme, et repart seul
//   (relance.jsx). Une règle : « Non envoyé · Renvoyer ». Interrupteur coupé (`lanac:outbox` = "0") : le comportement d'avant.
//   ⚠️ Un message DIFFÉRÉ par le serveur EST une relance (`relance: "serveur"`, à l'instant dit) : sans cela il resterait
//   hors de la règle — passé 24 heures il ne rendrait jamais la main et paraîtrait « Envoyé » sans être parti ; l'interrupteur,
//   le cache et la file (le message suivant attend derrière lui) le voient comme les autres.
const suiteDEchec = (message, res) => {
  if (!outboxArme()) return { failed: true, pending: false };
  const cadence = persistRefusedByRate(res);
  if (cadence) { const quand = Date.now() + delaiDuRefus(cadence); return { differe: quand, reessai: quand, relance: "serveur", failed: false, pending: false }; }
  return apresEchec(message, res, Date.now());
};
//  A42 — PARTI par l'envoi direct : toute marque d'attente ou d'échec qu'une passe aurait posée entre-temps s'efface (un message
//   arrivé ne reste ni « Envoi en cours… » ni « Non envoyé »).
const parti = () => ({ pending: false, failed: false, refused: undefined, definitif: undefined, relance: undefined, reessai: undefined, essais: undefined, convoEcrite: undefined, differe: undefined });
//  A42 — UN ESSAI QUI NE RÉPOND PAS NE RETIENT PAS LA FILE : passé ce délai il est tenu pour une panne de réseau (le message
//   reste en relance, la passe continue). Si la réponse arrive plus tard, l'identifiant rend le doublon inoffensif (23505).
const avecDelai = (promesse, ms) => new Promise((oui, non) => {
  const h = setTimeout(() => non(Object.assign(new Error("timed out"), { delai: true })), ms);
  promesse.then((v) => { clearTimeout(h); oui(v); }, (e) => { clearTimeout(h); non(e); });
});
//  A42 — la passe la plus forte l'emporte quand deux sont demandées pendant une troisième.
const forceDeLaPasse = (mode) => (mode === "tout" ? 2 : mode === "signal" ? 1 : 0);
// ── Helpers PARTAGÉS hook d'envoi ↔ rejeu app-level (28/08) — un seul écrivain de forme ──
// Patch ciblé d'un message dans `convos` (la source unique).
const patchMsg = (setConvos, convoId, msgId, patch) =>
  setConvos((p) => p.map((c) => c.id === convoId ? { ...c, messages: c.messages.map((x) => x.id === msgId ? { ...x, ...patch } : x) } : c));
// M5c — estampille le seq serveur + read_seq local (extrait tel quel pour être partagé).
const stampSentSeqWith = (setConvos, convoId, msgId, res) => {
  if (!lanacReadWatermark) return;
  const sq = extractSentSeq(res);
  if (sq == null) return;
  try { lanacReadSeqMap.set(convoId, Math.max(Number(lanacReadSeqMap.get(convoId) || 0), sq)); } catch (e) {}
  setConvos((p) => p.map((c) => c.id === convoId ? { ...c, messages: c.messages.map((x) => x.id === msgId ? (x.seq != null ? x : { ...x, seq: sq }) : x) } : c));
};

// ── M2+ · REJEU DE L'OUTBOX, AU NIVEAU APP (28/08, audit) ────────────────────────
//  AVANT : l'effet de rejeu vivait dans ce hook, monté par Messenger — un composant
//  LAZY. Le commentaire promettait « au boot suivant, les échecs sont rejoués », mais
//  un utilisateur qui rebootait sans OUVRIR la messagerie ne rejouait RIEN : le rejeu
//  « au boot » n'existait pas au niveau app. Ce hook est désormais monté par App()
//  (toujours vivant dès la session) : boot, retour réseau ET première hydratation du
//  cache convos déclenchent une passe. Même politique, mêmes bornes, même verrou.
export function useOutboxReplay({ meId, convos, setConvos }) {
  const rejeuEnCours = useRef(false);
  const convosRef = useRef(convos);
  convosRef.current = convos;
  const hydratationVue = useRef(false);
  //  ⭐ C7 · T27 — UNE SEULE HORLOGE, et elle n'est qu'une OPTIMISATION. L'instant est persisté sur le
  //   message : si le minuteur ne tire pas (onglet en arrière-plan bridé par le navigateur, application
  //   fermée), la passe du prochain démarrage ou du retour réseau ramasse le message. Un minuteur qui serait
  //   l'unique chemin serait un point de perte ; ici c'est une commodité.
  const minuteurRef = useRef(null);
  const rythmeRef = useRef(0);
  const echeanceRef = useRef(0);                               // A42 : l'instant pour lequel l'horloge est armée (0 : aucune)
  const plancherRef = useRef(0);                               // A42 : « pas avant » — l'instant que le SERVEUR a dit (T27)
  const monteRef = useRef(null);                               // A42 : le membre pour qui ce rejeu est monté (null : démonté)
  //  A42 : la méfiance envers les signaux qui se sont trompés, PAR SOURCE — la liaison temps réel (le serveur a répondu) garde
  //   son crédit quand un retour au premier plan ou un nouvel envoi s'est trompé. `avant` : l'instant avant lequel la source
  //   n'est pas crue ; `recul` : son recul courant.
  const mefianceRef = useRef({ liaison: { avant: 0, recul: 0 }, autre: { avant: 0, recul: 0 } });
  const enAttenteRef = useRef(null);                           // A42 : la passe demandée pendant qu'une autre était en cours
  const dernierRef = useRef(null);                             // A42 : le rejeu du rendu COURANT (le bon membre, après un changement de compte)
  //  `duServeur` (le défaut : les deux appels de T27, la cadence et le rythme) pose aussi le PLANCHER : l'horloge de la relance
  //   ne passera pas devant ce que le serveur a dit.
  const planifier = (ms, duServeur = true) => {
    if (typeof window === "undefined") return;
    if (minuteurRef.current) clearTimeout(minuteurRef.current);
    const dans = Math.max(250, ms);
    echeanceRef.current = Date.now() + dans;
    if (duServeur) plancherRef.current = echeanceRef.current;
    minuteurRef.current = setTimeout(() => { minuteurRef.current = null; echeanceRef.current = 0; rejouer("horloge").then(suivante, suivante); }, dans);
  };
  //  ⭐ A42 — L'HORLOGE A TIRÉ. S'il reste une attente À VENIR (l'essai qu'un signal a repoussé pendant qu'elle était armée pour
  //   plus tôt, la file d'un autre fil), elle se réarme pour elle : aucune attente ne reste sans horloge. Jamais pour « tout de
  //   suite » : ce qui est mûr et n'est pas parti (hors ligne, une passe déjà en cours) attend un signal ou un changement
  //   d'état — pas une horloge qui tournerait à vide.
  const suivante = () => {
    if (!meId || monteRef.current !== meId || !outboxArme()) return;
    const t = Date.now(), quand = prochaineRelance(convosRef.current, meId, t);
    if (quand !== null && quand > t) planifierAu(quand);
  };
  //  ⭐ A42 — armer l'horloge POUR un instant : seulement si rien n'est armé, ou si cet instant est plus tôt que celui déjà
  //   armé (la cadence du serveur garde la sienne). Toujours une seule horloge.
  const planifierAu = (instant) => {
    const cible = instantDArmement(instant, plancherRef.current, minuteurRef.current ? echeanceRef.current : 0);
    if (cible !== null) planifier(cible - Date.now(), false);
  };
  //  ⭐ A42 — TROIS SORTES DE PASSES, pour que la relance ne coûte que ce qu'elle doit.
  //   « tout » : le démarrage, le cache qui s'hydrate, l'évènement `online` — le contrat d'avant, mot pour mot, et les relances
  //     sans attendre leur instant (le contexte a changé : on essaie).
  //   « signal » : le retour au premier plan, la liaison temps réel rétablie — les relances tout de suite ; les refus
  //     définitifs (une règle a dit non) ne sont PAS refrappés à chaque retour.
  //   « horloge » : le filet — seulement ce qui est mûr à son instant.
  const rejouer = async (mode = "tout", { liaison = false } = {}) => {
    if (!meId) return;
    //  Interrupteur COUPÉ (`lanac:outbox` = "0") : plus rien ne part seul — ce qui ATTENDAIT rend la main au membre (« Non
    //   envoyé · Renvoyer », le comportement d'avant) : jamais un « Envoi en cours… » que plus rien n'enverrait.
    if (!outboxArme()) { for (const p of relancesPerimees(convosRef.current, meId, Date.now(), true)) patchMsg(setConvos, p.convoId, p.id, { failed: true, pending: false, relance: undefined, reessai: undefined }); return; }
    //  Une passe est déjà en cours : celle-ci n'est pas PERDUE — elle part dès que la première rend la main (sans quoi une
    //   horloge tirée pendant une passe ne réveillerait plus personne).
    if (rejeuEnCours.current) {
      const a = enAttenteRef.current;
      enAttenteRef.current = { mode: a && forceDeLaPasse(a.mode) > forceDeLaPasse(mode) ? a.mode : mode, liaison: liaison || !!(a && a.liaison) };
      return;
    }
    //  Une relance de plus de 24 heures rend la main au membre (« Non envoyé · Renvoyer ») : ressusciter en silence un message
    //   d'avant-hier serait une surprise, pas un service (la borne de M2, celle de Signal).
    for (const p of relancesPerimees(convosRef.current, meId, Date.now())) patchMsg(setConvos, p.convoId, p.id, { failed: true, pending: false, relance: undefined, reessai: undefined });
    //  Hors ligne DIT par le navigateur : aucun essai (zéro requête) — sauf quand la liaison temps réel vient de se rétablir :
    //   elle, ne ment pas (MDN : `navigator.onLine` est « peu fiable par nature »).
    if (!liaison && typeof navigator !== "undefined" && navigator.onLine === false) return; // inutile de tenter à vide
    //  Un signal qui vient de SE TROMPER (l'essai qu'il a provoqué a échoué) n'est pas recru aussitôt : 10 s, puis 20, 40…
    //   jusqu'à cinq minutes (relance.jsx · reculDuSignal). Une liaison qui clignote ne fait pas essayer à chaque retour.
    //   Pas cru, il ne fait pas RENONCER la passe : elle devient une passe d'horloge — ce qui est mûr part quand même.
    const mefiance = mefianceRef.current[liaison ? "liaison" : "autre"];
    const cru = mode === "signal" && Date.now() >= mefiance.avant;
    if (mode === "signal" && !cru) mode = "horloge";
    const lot = collectOutbox(convosRef.current, meId, { now: Date.now(), signal: mode !== "horloge", definitifs: mode === "tout", enVol: mode === "tout" });
    if (!lot.length) return;
    rejeuEnCours.current = true;
    let echoue = false, abouti = false;
    try {
      let refusCadence = null;
      const arretees = new Set();                              // A42 : les fils dont la tête a échoué dans cette passe
      const fermes = new Map();                                // A42 : les fils dont le serveur a différé l'OUVERTURE dans cette passe (→ l'instant dit)
      for (const entree of lot) {
        const { convo, message } = entree;
        //  Le serveur vient de différer l'ouverture de ce fil : ses autres messages attendent le MÊME instant, sans requête.
        if (fermes.has(convo.id)) { patchMsg(setConvos, convo.id, message.id, { differe: fermes.get(convo.id), reessai: fermes.get(convo.id), relance: typeof message.relance === "string" ? message.relance : "serveur", failed: false, pending: false }); continue; }
        //  A42 — un message EN RELANCE garde son état calme pendant l'essai (« Envoi en cours… ») ; et si la tête de son fil
        //   vient d'échouer dans cette passe, il attend derrière elle : la file ne double pas.
        const enRelance = typeof message.relance === "string";
        if (enRelance && arretees.has(convo.id)) continue;
        if (!enRelance) patchMsg(setConvos, convo.id, message.id, { failed: false, pending: false }); // optimiste : la barre disparaît
        //  A42 — un réessai dont l'essai d'avant a échoué à l'écriture du MESSAGE ne réécrit pas la conversation (déjà écrite).
        const res = await avecDelai(persistConvoAndMessage(convo, message, { sansConvo: enRelance && message.convoEcrite === true }), RELANCE_ESSAI_MS).catch((error) => ({ error }));
        if (refusDuPersist(res)) patchMsg(setConvos, convo.id, message.id, refusDuPersist(res)); // la règle a parlé (fil bloqué, texte refusé) : on ne rejoue plus
        else if ((refusCadence = persistRefusedByRate(res))) {
          //  ⭐ T25 — ON ARRÊTE LE LOT. Les messages suivants de la même passe seraient refusés pour
          //   EXACTEMENT la même raison : les envoyer, c'est payer dix-neuf requêtes pour s'entendre dire
          //   dix-neuf fois la même chose.
          //  ⭐⭐ T27 — ET LE MESSAGE REPART TOUT SEUL, à la seconde que le serveur a dite. Plus de bouton
          //   « Renvoyer » ni d'alarme rouge pour une attente de deux secondes : `differe` porte l'instant,
          //   le minuteur ci-dessous le réveille, et le marqueur du fil dit calmement quand.
          //   ⚠️ `failed:false` est ESSENTIEL : sans lui, le fil afficherait l'échec rouge par-dessus.
          const attente = delaiDuRefus(refusCadence);
          //  A42 — LA CADENCE D'OUVERTURE D'UN FIL (le refus vient de l'écriture de la CONVERSATION : tant de fils neufs par
          //   demi-heure) ne dit rien du rythme des MESSAGES : elle retient CE message, à son instant — sans apprendre un
          //   rythme de cinq minutes à toute la file, sans retenir les autres fils, sans arrêter le lot.
          const duFil = res.etape === "conversation";
          if (!duFil) rythmeRef.current = attente;   // ⭐ on a APPRIS le rythme du serveur : la file s'y cadencera
          patchMsg(setConvos, convo.id, message.id, { differe: Date.now() + attente, failed: false, pending: false });
          //  A42 — différé par le serveur, il EST une relance : il garde sa place dans la file, rend la main après 24 heures,
          //   et l'interrupteur comme le cache le voient (voir suiteDEchec).
          patchMsg(setConvos, convo.id, message.id, { relance: enRelance ? message.relance : "serveur", reessai: Date.now() + attente }); arretees.add(convo.id);
          if (duFil) { fermes.set(convo.id, Date.now() + attente); continue; }
          planifier(attente);
          break;
        }
        else if (persistFailed(res)) {
          //  ⭐ A42 — UNE PANNE N'EST PLUS UN ÉCHEC. Réseau ou serveur : le message attend (calme) et repartira seul, à son
          //   instant ou au premier signal ; sa file s'arrête derrière lui. Une règle : le bouton revient, comme avant.
          const suite = suiteDEchec(message, res);
          patchMsg(setConvos, convo.id, message.id, suite);
          if (suite.relance) arretees.add(convo.id);
          echoue = echoue || !!suite.relance;
          //  Un essai resté SANS RÉPONSE trente secondes : le réseau avale tout — on n'y envoie pas le reste du lot.
          if (res.error && res.error.delai === true) break;
        }
        else {
          abouti = true;
          //  A42 — PARTI : il quitte la file, et l'instant que le serveur avait dit (`differe`) S'EFFACE. T27 ne l'effaçait
          //   pas : un message différé puis parti était repris pour « échu » à chaque passe (un renvoi pour rien à chaque
          //   démarrage pendant 24 heures) — et, avec une horloge qui suit l'état, c'eût été une boucle.
          if (enRelance || message.differe) patchMsg(setConvos, convo.id, message.id, { relance: undefined, reessai: undefined, essais: undefined, convoEcrite: undefined, differe: undefined, failed: false, pending: false });
          stampSentSeqWith(setConvos, convo.id, message.id, res);
          //  ⭐⭐ LA FILE REPART AU RYTHME DU SERVEUR — c'est ici qu'on dépasse Signal et WhatsApp. Une fois le
          //   rythme APPRIS (par l'unique refus qui nous l'a enseigné), on n'envoie plus qu'un message par
          //   passe, espacés de ce rythme : AUCUN des suivants n'est refusé. Eux rejouent le lot entier et
          //   encaissent N−1 refus à chaque passe, parce qu'ils ne connaissent pas le rythme — ils le devinent.
          if (rythmeRef.current > 0 && lot.indexOf(entree) < lot.length - 1) { planifier(rythmeRef.current); break; }
        }
      }
    } finally {
      rejeuEnCours.current = false;
      //  Le recul des signaux : un essai ABOUTI (quelle que soit la passe) leur rend leur crédit ; un signal qui s'est trompé
      //   le perd pour un temps qui double.
      if (abouti) { for (const source of ["liaison", "autre"]) { mefianceRef.current[source].avant = 0; mefianceRef.current[source].recul = 0; } }
      else if (echoue && cru) { mefiance.recul = reculDuSignal(mefiance.recul, true); mefiance.avant = Date.now() + mefiance.recul; }
      //  La passe demandée pendant celle-ci part à son tour, une fois l'état rendu — par le rejeu du rendu COURANT (après un
      //   changement de compte, c'est celui du nouveau membre). Une horloge avalée repasse par l'armement : le plancher du
      //   serveur tient.
      const a = enAttenteRef.current; enAttenteRef.current = null;
      if (a && typeof window !== "undefined") setTimeout(() => {
        const d = dernierRef.current;
        if (!d || !monteRef.current) return;
        if (a.mode === "horloge") d.planifierAu(Date.now());
        else d.rejouer(a.mode, { liaison: a.liaison }).then(d.suivante, d.suivante);
      }, 250);
    }
  };
  dernierRef.current = { rejouer, suivante, planifierAu };
  useEffect(() => {
    if (!meId) return;
    monteRef.current = meId;
    const auRetourReseau = () => { rejouer(); };
    if (typeof window !== "undefined") window.addEventListener("online", auRetourReseau);
    //  ⭐ A42 — LES SIGNAUX QUI NE DEVINENT PAS (ils ne coûtent rien : une passe sans rien à envoyer ne fait aucune requête).
    //   Le retour au premier plan : le membre revient, le réseau a pu revenir avec lui. La liaison temps réel rétablie
    //   (`lanac:liaison`, émis par useRealtimeInbox quand le canal est de nouveau inscrit) : le serveur RÉPOND — la preuve
    //   que `online` ne donne pas.
    const auPremierPlan = () => { if (document.visibilityState === "visible") rejouer("signal"); };
    const aLaLiaison = () => { rejouer("signal", { liaison: true }); };
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", auPremierPlan);
    if (typeof window !== "undefined") window.addEventListener("lanac:liaison", aLaLiaison);
    //  Un NOUVEL ENVOI derrière un message qui attend (`lanac:geste`, émis par `send`) : le membre est là — on essaie la tête.
    const auGeste = () => { rejouer("signal"); };
    if (typeof window !== "undefined") window.addEventListener("lanac:geste", auGeste);
    rejouer();                                                // au montage (boot app)
    return () => {
      if (typeof window !== "undefined") window.removeEventListener("online", auRetourReseau);
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", auPremierPlan);
      if (typeof window !== "undefined") window.removeEventListener("lanac:liaison", aLaLiaison);
      if (typeof window !== "undefined") window.removeEventListener("lanac:geste", auGeste);
      monteRef.current = null;
      //  Une horloge qui survivrait au démontage réveillerait un composant disparu.
      if (minuteurRef.current) { clearTimeout(minuteurRef.current); minuteurRef.current = null; }
    };
  }, [meId]);
  // Le cache convos s'hydrate APRÈS le montage (asynchrone) : la PREMIÈRE hydratation
  // déclenche une passe — sinon le rejeu « au boot » courrait avant que les échecs
  // restaurés du cache n'existent. Une seule fois : les passes suivantes viennent de
  // `online` ou d'un prochain boot (jamais de boucle sur convos).
  useEffect(() => {
    if (!meId || hydratationVue.current || !(convos && convos.length)) return;
    hydratationVue.current = true;
    rejouer();
  }, [meId, convos]);
  //  ⭐ A42 — L'HORLOGE SUIT L'ÉTAT, elle ne le pilote pas. Quel que soit celui qui pose une attente sur un message (l'envoi,
  //   le renvoi au geste, une passe, le cache relu au démarrage), l'horloge s'arme pour la plus proche — la tête de chaque
  //   file. Elle n'écrit RIEN ici : pas de boucle (une passe qui n'envoie rien ne change pas l'état, donc ne réarme pas).
  //   Elle reste une commodité (T27) : si elle ne tire pas, le démarrage et les signaux ramassent le message.
  useEffect(() => {
    if (!meId || !outboxArme()) return;
    const quand = prochaineRelance(convos, meId, Date.now());
    if (quand !== null) planifierAu(quand);
  }, [meId, convos]);
}

// ── Hook de câblage : renvoie {send, retryFailedMessage, markMsgFailed} FRAIS à chaque rendu
//    (patron latest-ref : Messenger les réassigne à composerSendRef / _rowFnsFresh → toujours l'état frais,
//    SANS re-rendre la feuille de saisie). Aucune logique changée vs l'ancien corps de Messenger. ──
function useMessageDelivery({ me, lang, actId, active, convos, exchangeBlocked, pendingImage, replyTo, setConvos, setPendingImage, setReplyTo, setAttachErr, preview, uploadChatImage, M, lectureSeule = false }) {
  // Toute RÉSOLUTION (échec avéré comme retour au calme) efface `pending` : le marqueur ne
  // dit qu'une chose — « la réponse du serveur n'est pas encore arrivée ».
  const markMsgFailed = (convoId, msgId, val) => patchMsg(setConvos, convoId, msgId, { failed: val, pending: false });
  const markMsgRefused = (convoId, msgId, res) => patchMsg(setConvos, convoId, msgId, refusDuPersist(res) || { refused: "blocked", failed: false, pending: false });

  const retryFailedMessage = (m) => { if (!active || !m || lectureSeule) return; markMsgFailed(active.id, m.id, false); persistConvoAndMessage(active, m).then((res) => { if (refusDuPersist(res)) { markMsgRefused(active.id, m.id, res); return; } if (persistFailed(res)) patchMsg(setConvos, active.id, m.id, suiteDEchec(m, res)); else patchMsg(setConvos, active.id, m.id, parti()); }); };

  // M5c — estampille le message optimiste avec le seq serveur renvoyé (à ON) → le « Vu » (✓✓) live peut
  //  s'y appliquer ; met aussi à jour mon read_seq local (corps partagé stampSentSeqWith).
  const stampSentSeq = (convoId, msgId, res) => stampSentSeqWith(setConvos, convoId, msgId, res);

  // `send` reçoit le texte en ARGUMENT (la saisie vit dans <MessageComposerInput>) et RETOURNE un booléen
  //  (true = envoyé → la feuille se vide ; false = bloqué → texte conservé).
  const send = (textArg) => {
    const dec = decideSend({ text: textArg, hasImage: !!pendingImage, actId, active, exchangeBlocked, safetyTextIssue, M, lectureSeule });
    if (!dec.ok) { if (dec.error != null) setAttachErr(dec.error); return false; }
    const text = (textArg || "").trim();
    const img = pendingImage;
    const convoForSend = active;
    const m0 = buildOptimisticMessage({ id: newUUID(), from: me.id, text, image: img, replyTo, preview, now: Date.now() });
    //  ⭐ A42 — L'ORDRE EST TENU. Si un de mes messages attend encore dans ce fil (une panne le retient), le nouveau prend la
    //   file DERRIÈRE lui (`relance: "file"`) au lieu de partir tout de suite et de le doubler : la file du fil l'enverra à son
    //   tour (Signal : une file par destinataire). Une photo prend la file une fois téléversée.
    const derriere = outboxArme() && aUneRelance(convoForSend, me.id);
    const m = derriere && !(img && img.file) ? { ...m0, pending: false, relance: "file" } : m0;
    setConvos((p) => p.map((c) => c.id === actId ? { ...c, messages: [...c.messages, m] } : c));
    setPendingImage(null); setReplyTo(null); setAttachErr("");
    if (img && img.file) {
      // Photo : aperçu optimiste immédiat (base64 local), puis upload Storage → persiste l'URL.
      //  Repli automatique sur le base64 si l'upload échoue (le message part quand même).
      uploadChatImage(img.file, m.id).then((url) => {
        const finalImage = url ? { url, name: img.name } : m.image;
        if (url) setConvos((p) => p.map((c) => c.id === convoForSend.id ? { ...c, messages: c.messages.map((x) => x.id === m.id ? { ...x, image: finalImage } : x) } : c));
        if (derriere) { patchMsg(setConvos, convoForSend.id, m.id, { pending: false, relance: "file" }); return; }   // A42 : à son tour, derrière le message qui attend
        persistConvoAndMessage(convoForSend, { ...m, image: finalImage }).then((res) => { if (refusDuPersist(res)) { markMsgRefused(convoForSend.id, m.id, res); return; } if (persistFailed(res)) { patchMsg(setConvos, convoForSend.id, m.id, suiteDEchec({ ...m, image: finalImage }, res)); return; } patchMsg(setConvos, convoForSend.id, m.id, parti()); stampSentSeq(convoForSend.id, m.id, res); });
      });
    } else if (!derriere) {
      persistConvoAndMessage(convoForSend, m).then((res) => { if (refusDuPersist(res)) { markMsgRefused(convoForSend.id, m.id, res); return; } if (persistFailed(res)) { patchMsg(setConvos, convoForSend.id, m.id, suiteDEchec(m, res)); return; } patchMsg(setConvos, convoForSend.id, m.id, parti()); stampSentSeq(convoForSend.id, m.id, res); });
    }
    //  A42 — un nouvel envoi DERRIÈRE un message qui attend depuis trois secondes au moins est un signal : le membre est là,
    //   on essaie la tête de la file (le recul des signaux borne la chose : dix messages tapés pendant une panne ne font
    //   pas dix essais).
    if (derriere && attenteDepuis(convoForSend, me.id, Date.now()) >= GESTE_APRES_MS) { try { window.dispatchEvent(new Event("lanac:geste")); } catch (_) {} }
    // La notification « Nouveau message » du destinataire est émise CÔTÉ SERVEUR (trigger trg_notify_message
    //  sur messages) — garantie même si cet onglet se ferme juste après l'envoi.
    return true;
  };

  // (M2+ 28/08 : le REJEU de l'outbox a quitté ce hook — il vit au niveau App, dans
  //  `useOutboxReplay` ci-dessus, monté que la messagerie soit ouverte ou non. Ce hook
  //  ne garde que l'ENVOI et le retry au geste.)

  return { send, retryFailedMessage, markMsgFailed };
}

export { useMessageDelivery };
